import {
  Activity,
  BarChart3,
  BedDouble,
  BellRing,
  BriefcaseBusiness,
  Building2,
  CalendarCheck,
  CalendarDays,
  CheckCircle2,
  Clock,
  ConciergeBell,
  CreditCard,
  FileText,
  Gift,
  Globe,
  Handshake,
  LayoutDashboard,
  Map,
  Moon,
  PackagePlus,
  PhoneCall,
  QrCode,
  Receipt,
  Settings,
  ShieldCheck,
  ShoppingCart,
  Tags,
  UserRound,
  Users,
  Utensils,
  Wrench,
} from "lucide-react";
import { HOTEL_PERMISSIONS as P } from "@/constants/hotelPermissions";

export const baseHotelNavItems = [
  {
    key: "dashboard",
    path: "/hotel/dashboard",
    icon: LayoutDashboard,
    label: "Dashboard",
  },
  {
    key: "frontDesk",
    icon: ConciergeBell,
    label: "Front Desk & Ops",
    children: [
      {
        path: "/hotel/check-availability",
        icon: CalendarDays,
        label: "Check availability",
        permission: P.availability?.view || "hotel.availability.view",
        capability: "hotel",
      },
      {
        path: "/hotel/sessions",
        icon: Users,
        label: "Guest sessions",
        permission: P.sessions?.view || "hotel.sessions.view",
        capability: "hotel",
      },
      {
        path: "/hotel/qr-generator",
        icon: QrCode,
        label: "Check-in QR",
        permission: P.qr?.view || "hotel.qr.view",
        capability: "hotel",
      },
      {
        path: "/hotel/requests",
        icon: BellRing,
        label: "In-Room Requests",
        permission: P.requests.view,
        capability: "guestPortal",
      },
      {
        path: "/hotel/housekeeping",
        icon: CheckCircle2,
        label: "Housekeeping & Maintenance",
        permission: [P.housekeeping?.view, P.rooms.view].filter(Boolean),
        capability: "hotel",
      },
    ],
  },
  {
    key: "callsAndAlerts",
    icon: PhoneCall,
    label: "Calls & Alerts",
    capability: "calls",
    children: [
      {
        path: "/hotel/calls",
        icon: PhoneCall,
        label: "Department alert center",
        permission: P.calls.view,
        capability: "calls",
      },
      {
        path: "/hotel/calls/directory",
        icon: PhoneCall,
        label: "Call directory",
        permission: P.calls.view,
        capability: "calls",
      },
      {
        path: "/hotel/contact-links",
        icon: Globe,
        label: "Website Call Links",
        permission: P.settings.view,
        capability: "calls",
      },
    ],
  },
  {
    key: "reservations",
    icon: CalendarCheck,
    label: "Reservations & Sales",
    capability: "bookings",
    children: [
      {
        path: "/hotel/bookings",
        icon: CalendarCheck,
        label: "Booking management",
        permission: "hotel.bookings.view",
        capability: "bookings",
      },
      {
        path: "/hotel/bookers",
        icon: BriefcaseBusiness,
        label: "Corporate bookers",
        permission: P.bookers.view,
        capability: "bookings",
      },
      {
        path: "/hotel/booker-rewards",
        icon: Gift,
        label: "Booker rewards",
        permission: P.bookers.view,
        capability: "bookings",
      },
      {
        path: "/hotel/bookings/reports",
        icon: BarChart3,
        label: "Booking reports",
        permission: "hotel.bookings.reports-view",
        capability: "bookings",
      },
      {
        path: "/hotel/bookers/reports",
        icon: BarChart3,
        label: "Booker reports",
        permission: P.bookers.reportsView,
        capability: "bookings",
      },
    ],
  },
  {
    key: "guestCrm",
    icon: UserRound,
    label: "Guest CRM",
    children: [
      {
        path: "/hotel/guests",
        icon: UserRound,
        label: "Guest directory",
        permission: P.guestCrm.view,
        capability: "guestCrm",
      },
      {
        path: "/hotel/know-your-guest",
        icon: ShieldCheck,
        label: "Know Your Guest",
        permission: "know-your-guest.view",
        capability: "guestCrm",
      },
      {
        path: "/hotel/surveys",
        icon: FileText,
        label: "Guest feedback",
        permission: P.feedback.view,
        capability: "surveys",
      },
      {
        path: "/hotel/loyalty",
        icon: Gift,
        label: "Loyalty program",
        permission: P.guestCrm.view,
        capability: "loyalty",
      },
      {
        path: "/hotel/memberships",
        icon: Tags,
        label: "Membership packages",
        permission: P.guestCrm.view,
        capability: "loyalty",
      },
      {
        path: "/hotel/membership-events",
        icon: CalendarDays,
        label: "Membership events",
        permission: P.guestCrm.view,
        capability: "loyalty",
      },
      {
        path: "/hotel/coupons",
        icon: Tags,
        label: "Coupons",
        permission: P.guestCrm.view,
        capability: "loyalty",
      },
      {
        path: "/hotel/partner-redemptions",
        icon: Handshake,
        label: "Partner redemptions",
        permission: P.guestCrm.view,
        capability: "loyalty",
      },
      {
        path: "/hotel/guests/reports",
        icon: BarChart3,
        label: "CRM reports",
        permission: P.guestCrm.reportsView,
        capability: "guestCrm",
      },
    ],
  },
  {
    key: "pos",
    icon: ShoppingCart,
    label: "Point of Sale",
    capability: "pos",
    children: [
      {
        path: "/hotel/pos/dashboard",
        icon: Activity,
        label: "POS Dashboard",
        permission: P.pos.dashboard.view,
        capability: "pos",
      },
      {
        path: "/hotel/pos/orders",
        icon: ShoppingCart,
        label: "Orders",
        permission: P.pos.orders.view,
        capability: "pos",
      },
      {
        path: "/hotel/pos/clients",
        icon: Users,
        label: "Clients",
        permission: P.pos.clients.view,
        hideIf: "hotel",
        capability: "pos",
      },
      {
        path: "/hotel/pos/menu-management",
        icon: Utensils,
        label: "Menu management",
        permission: P.pos.menu.view,
        capability: "pos",
      },
      {
        path: "/hotel/pos/extras",
        icon: PackagePlus,
        label: "Extras",
        permission: P.pos.menu.view,
        capability: "pos",
      },
    ],
  },
  {
    key: "finance",
    icon: Receipt,
    label: "Finance",
    capability: "guestBilling",
    children: [
      {
        path: "/hotel/billing/dashboard",
        icon: BarChart3,
        label: "Billing Dashboard",
        permission: P.billing.view,
        capability: "guestBilling",
      },
      {
        path: "/hotel/billing",
        icon: Receipt,
        label: "Search Bills",
        permission: P.billing.view,
        capability: "guestBilling",
      },
      {
        path: "/hotel/night-audit",
        icon: Moon,
        label: "Night audit & close",
        permission: P.nightAudit?.view || P.billing.view,
        capability: "guestBilling",
      },
      {
        path: "/hotel/finance/payment-methods",
        icon: CreditCard,
        label: "Payment methods",
        permission: P.settings.view,
        capability: "guestBilling",
      },
      {
        path: "/hotel/finance/settings",
        icon: Settings,
        label: "Finance Settings",
        permission: P.settings.view,
        capability: "guestBilling",
      },
      {
        path: "/hotel/finance/bulk-pricing",
        icon: Tags,
        label: "Bulk Price Manager",
        permission: P.settings.view,
        capability: "guestBilling",
      },
    ],
  },
  {
    key: "amenities",
    icon: PackagePlus,
    label: "Amenities & Services",
    capability: "hotel",
    children: [
      {
        path: "/hotel/services",
        icon: ConciergeBell,
        label: "Hotel services",
        permission: P.amenities.view,
        capability: "hotel",
      },
      {
        path: "/hotel/partner-services",
        icon: Handshake,
        label: "External Partners & Dining",
        permission: P.amenities.view,
        capability: "hotel",
      },
      {
        path: "/hotel/recommended-activities",
        icon: Map,
        label: "Local activities",
        permission: P.recommendations.view,
        capability: "hotel",
      },
    ],
  },
  {
    key: "configuration",
    icon: Settings,
    label: "Configuration",
    children: [
      {
        path: "/hotel/rooms",
        icon: BedDouble,
        label: "Rooms setup",
        permission: P.rooms.view,
        capability: "hotel",
      },
      {
        path: "/hotel/room-types",
        icon: Tags,
        label: "Room types",
        permission: "hotel.room-types.view",
        capability: "hotel",
      },
      {
        path: "/hotel/room-access-qr",
        icon: QrCode,
        label: "Room access QR",
        permission: P.qr.view,
        capability: "guestPortal",
      },
      {
        path: "/hotel/admin/users",
        icon: Users,
        label: "Staff users",
        permission: P.users.view,
      },
      {
        path: "/hotel/admin/departments",
        icon: Building2,
        label: "Departments",
        permission: P.departments.view,
      },
      {
        path: "/hotel/admin/roles",
        icon: ShieldCheck,
        label: "Roles & Permissions",
        permission: P.roles.view,
      },
      {
        path: "/hotel/settings",
        icon: Settings,
        label: "General Settings",
        permission: P.settings.view,
      },
    ],
  },
];

export const buildHotelNavItems = ({
  hasPermission,
  organizationCapabilities,
}) => {
  const isPosOnly =
    organizationCapabilities?.hotel === false &&
    organizationCapabilities?.pos === true;

  return baseHotelNavItems
    .map((item) => {
      let resolvedItem = item;
      if (isPosOnly && item.path === "/hotel/dashboard") {
        resolvedItem = {
          ...item,
          path: "/hotel/pos/dashboard",
          label: "POS Dashboard",
          icon: ShoppingCart,
        };
      }

      if (!resolvedItem.children?.length) return resolvedItem;

      let children = resolvedItem.children.filter((child) => {
        if (child.capability && !organizationCapabilities?.[child.capability]) {
          return false;
        }
        if (child.hideIf && organizationCapabilities?.[child.hideIf]) {
          return false;
        }

        if (Array.isArray(child.permission)) {
          return child.permission.some((perm) => hasPermission?.(perm));
        }
        return !child.permission || hasPermission?.(child.permission);
      });

      if (isPosOnly && item.key === "pos") {
        children = children.filter(
          (child) => child.path !== "/hotel/pos/dashboard",
        );
      }

      return {
        ...resolvedItem,
        children,
      };
    })
    .filter((item) => {
      if (item.capability && !organizationCapabilities?.[item.capability]) {
        return false;
      }

      if (item.hideIf && organizationCapabilities?.[item.hideIf]) {
        return false;
      }

      if (item.permission) {
        const allowed = Array.isArray(item.permission)
          ? item.permission.some((perm) => hasPermission?.(perm))
          : hasPermission?.(item.permission);
        if (!allowed) return false;
      }
      if (item.children && item.children.length === 0) return false;
      return true;
    });
};
